//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import GalleryContainerId from '@galleries/models/GalleryContainerId';
import DataSourceId from '@models/DataSourceId';
import ExperienceContainerId from '@models/ExperienceContainerId';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { ExperienceLayoutType } from '@models/ExperienceLayoutType';
import ReelId from '@models/ReelId';

export interface IExperiencesResponse {
  experiences: IExperienceDto[];
  siteName?: string;
  latestDeployment?: IDeploymentDetails;
}

export interface IDeploymentDetails {
  deployer: string;
  deploymentTimestamp: number;
}

export interface IExperiencePreviewRequest {
  containerId: ExperienceContainerId | GalleryContainerId;
  layout: ExperienceLayoutType;
  source: string;
  customizationOptions: IExperienceCustomizationsDto;
}

export interface IExperiencePreviewResponse {
  snippet: string;
}

export interface IExperienceDto {
  experienceType: ExperienceContainerType;
  experienceLayout: ExperienceLayoutType;
  // This is the reel id usually, other than a few cases where it is a 'datasource.' value
  experienceDataSource: ReelId | string;
  // This is the true underlying data source id that media api knows
  experienceDataSourceId?: DataSourceId;
  experienceCustomizationOptions?: IExperienceCustomizationsDto;
}

export interface IExperienceCustomizationsDto {
  ctaButtonColor?: string;
  ctaText?: string;
  ctaTextColor?: string;
  enableReportMedia?: boolean;
  enableUploader?: boolean;
  filterLabelSet?: string[];
  font?: string;
  galleryLinkCta?: string;
  galleryLinkUrl?: string;
  gutterSize?: number;
  headerCtaPosition?: HeaderCtaPosition;
  headerFontSize?: number;
  headerGalleryLinkCtaBackgroundColor: string;
  headerGalleryLinkCtaBorderColor: string;
  headerGalleryLinkCtaButtonStyle: CtaButtonStyle;
  headerGalleryLinkCtaTextColor: string;
  headerSubtext?: string;
  headerText?: string;
  headerTextColor?: string;
  headerUploaderCtaBackgroundColor?: string;
  headerUploaderCtaBorderColor?: string;
  headerUploaderCtaButtonStyle?: CtaButtonStyle;
  headerUploaderCtaTextColor?: string;
  hoverCtaBackgroundColor?: string;
  hoverCtaBorderColor?: string;
  hoverCtaButtonStyle?: CtaButtonStyle;
  hoverCtaTextColor?: string;
  hoverOverlayColor?: string;
  hoverTextColor?: string;
  hoverText?: string;
  modalAddToCartIsEnabled?: boolean;
  modalAddToCartButtonText?: string;
  modalAddToCartButtonStyle?: CtaButtonStyle;
  modalAddToCartTextColor?: string;
  modalAddToCartBackgroundColor?: string;
  modalAddToCartBorderColor?: string;
  modalAddToCartViewMoreButtonText?: string;
  modalAddToCartViewMoreButtonStyle?: CtaButtonStyle;
  modalAddToCartViewMoreTextColor?: string;
  modalAddToCartViewMoreBackgroundColor?: string;
  modalAddToCartViewMoreBorderColor?: string;

  platformType?: PlatformType;
  salesforceSiteId?: string;
  galleryHoverShouldRoundLikes?: boolean;
  galleryHoverShowCta?: boolean;
  galleryHoverShowInstagramIcon?: boolean;
  galleryHoverShowLikes?: boolean;
  galleryHoverShowPlusIcon?: boolean;
  galleryHoverShowUser?: boolean;
  galleryThumbnailImageIsSquare?: boolean;
  galleryThumbnailShowVideoIcon?: boolean;

  carouselHoverShouldRoundLikes?: boolean;
  carouselHoverShowCta?: boolean;
  carouselHoverShowInstagramIcon?: boolean;
  carouselHoverShowLikes?: boolean;
  carouselHoverShowPlusIcon?: boolean;
  carouselHoverShowUser?: boolean;
  carouselNavigationBackgroundColor?: string;
  carouselNavigationBorderColor?: string;
  carouselNavigationIconColor?: string;
  carouselNavigationIconStyle?: IconStyle;
  carouselNavigationPosition?: CarouselNavigationPosition;
  carouselThumbnailImageIsSquare?: boolean;
  carouselThumbnailShowCaption?: boolean;
  carouselThumbnailShowVideoIcon?: boolean;

  iconColor?: string;
  includeEcommerceMetadata?: boolean;
  includeSocialMetadata?: boolean;
  itemTrayTitle?: string;
  loadMoreBackgroundColor?: string;
  loadMoreBorderColor?: string;
  loadMoreButtonStyle?: CtaButtonStyle;
  loadMoreTextColor?: string;
  loadMoreCtaText: string;
  locale: string;
  localizationStrings: Record<string, unknown>;
  minNumberOfColumns?: number;
  modalBackgroundColor?: string;
  modalBvLogoColor?: string;
  modalEcommerceTitleColor?: string;
  modalEcommerceProductPriceColor?: string;
  modalEcommerceProductSalePriceColor?: string;
  modalEcommerceShopButtonColor?: string;
  modalEcommerceShopButtonTextColor?: string;
  modalHeroIsSquare?: boolean;
  modalHeroProgressBarInProgressBackground?: string;
  modalHeroVideoAutoplay?: boolean;
  modalScrollHintTextColor?: string;
  modalTextColor?: string;
  modalType?: string;
  numberOfColumns?: number;
  numberOfRows?: number;
  pagination?: Pagination;
  productDisplayStyle?: string;
  reportMediaReelId?: ReelId;
  showBrandName?: boolean;
  showCaption: boolean;
  showGalleryLink: boolean;
  showHeader: boolean;
  showLikeCount: boolean;
  showProductTags: boolean;
  showReportMedia: boolean;
  showShareIcons: boolean;
  showSubtitle: boolean;
  showTimestamp: boolean;
  showTitle: boolean;
  showUploaderCta: boolean;
  showUser: boolean;
  sort?: MediaOrdering;
  subheaderFontSize?: number;
  subheaderTextColor?: string;
  uploaderCtaText?: string;
  uploaderReelId?: ReelId;
  uploaderTermsAndConditionsLink?: string;
}

export enum CarouselNavigationPosition {
  INLINE_OUTSIDE = 'inlineOutside',
  INLINE_OVERLAP = 'inlineOverlap',
}

export enum CtaButtonStyle {
  PILL = 'pill',
  RECTANGLE = 'rectangle',
  TEXT = 'text',
}

export enum HeaderCtaPosition {
  CENTER = 'center',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum IconStyle {
  RECTANGLE = 'rectangle',
  ROUND = 'round',
  TRANSPARENT = 'transparent',
}

export enum MediaOrdering {
  Latest = 0,
  Optimized = 1,
  Moderation = 2,
}

export enum Pagination {
  InfiniteScroll = 'infiniteScroll',
  LoadMore = 'loadMore',
}

export enum PlatformType {
  SHOPIFY_HOSTED = 'ShopifyHosted',
  SFCC_LOREAL = 'SfccLoreal',
  SFCC_CONNECTOR = 'SfccConnector',
}
